<template>
  <div class="base-tab">
    <div class="base-tab-head" :class="styled + '-style'">
      <div class="base-tab-list-wrapper">
        <ul class="base-tab-list" role="tablist" aria-orientation="horizontal" :style="gridTemplate">
          <li
            v-for="tab in tabOptions"
            :key="tab.val"
            class="base-tab"
            :class="getTabClass(tab)"
            role="presentation"
          >
            <BaseLink
              v-if="hash"
              role="tab"
              class="tab-button"
              :aria-controls="'panel-' + tab.val"
              :aria-selected="modelValue == tab.val"
              :to="'#' + tab.val"
            >
              <slot name="head" :tab="tab">
                <BaseIcon v-if="tab.icon" :name="tab.icon" size="size-6" class="tab-button-icon" />
                <span>{{ tab.label }}</span>
              </slot>
            </BaseLink>
            <button
              v-else
              role="tab"
              class="tab-button"
              :aria-controls="'panel-' + tab.val"
              :aria-selected="modelValue == tab.val"
              @click="clickTab(tab.val, $event)"
            >
              <slot name="head" :tab="tab">
                <BaseIcon v-if="tab.icon" :name="tab.icon" size="size-6" />
                <span>{{ tab.label }}</span>
              </slot>
            </button>
          </li>
        </ul>
      </div>
    </div>
    <div
      ref="tabPanelsRef"
      class="base-tab-panels"
      @mousedown="onMoveStart"
      @mouseup="onMoveEnd"
      @mouseleave="onMoveEnd"
      @touchstart="onMoveStart"
      @touchend="onMoveEnd"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
// 参考 https://github.com/HJ29/vue3-tabs
// https://developer.mozilla.org/ja/docs/Web/Accessibility/ARIA/Roles/Tab_Role
import type { SVG_NAMES } from '~/components/icons'

export interface TabOption {
  val: string
  label: string
  disabled?: boolean
  class?: string
  icon?: SVG_NAMES
  alert?: boolean
}

interface Position {
  x: number
  y: number
}

export interface TabPanelsState {
  startPosition: Position
  start: boolean
  tabOptions: TabOption[]
  modelValue?: string
  previousModelValue?: string
}

function getPosition(event: MouseEvent | TouchEvent): Position | undefined {
  if (event.type.includes('touch')) {
    const touch = (event as TouchEvent).changedTouches[0]
    return touch
      ? {
          x: touch.clientX,
          y: touch.clientY,
        }
      : undefined
  } else if (event.type.includes('mouse')) {
    return {
      x: (event as MouseEvent).clientX,
      y: (event as MouseEvent).clientY,
    }
  } else {
    return undefined
  }
}
</script>

<script lang="ts" setup>
interface Props {
  modelValue?: string
  tabOptions: TabOption[]
  animate?: boolean
  swipeable?: boolean
  threshold?: number
  hash?: boolean
  styled?: 'tab' | 'switch'
  tabWidth?: string
}

const props = withDefaults(defineProps<Props>(), {
  animate: false,
  swipeable: false,
  threshold: 50,
  hash: false,
  styled: 'tab',
})

const emits = defineEmits(['update:modelValue', 'on-animation-end'])

const tabPanelsRef = ref(null)

const getTabClass = (tab: TabOption) => {
  const clazz = []
  if (props.modelValue === tab.val) {
    clazz.push('is-active')
  }
  if (tab.disabled) {
    clazz.push('is-disabled')
  }
  if (tab.alert) {
    clazz.push('is-alert')
  }
  if (tab.class) {
    clazz.push(tab.class)
  }
  return clazz
}

const activeTabs = computed(() => {
  return props.tabOptions.filter(opt => !opt.disabled)
})

const gridTemplate = computed(() => {
  if (props.styled === 'switch') {
    if (props.tabWidth) {
      return { display: 'flex', width: props.tabWidth }
    } else {
      return {
        display: 'grid',
        'grid-template-columns': `repeat(${props.tabOptions.length},1fr)`,
      }
    }
  } else {
    return {}
  }
})

const state: TabPanelsState = reactive({
  tabPanelsRef,
  start: false,
  startPosition: {
    x: 0,
    y: 0,
  },
  tabOptions: props.tabOptions,
  modelValue: props.modelValue,
  previousModelValue: undefined,
})

const { modelValue, tabOptions } = toRefs(props)

provide('tab-state', state)

// 初期化

const width = ref(0)

let resizeObserver

onMounted(() => {
  watch(modelValue, (modelValue, previousModelValue) => {
    state.modelValue = modelValue
    state.previousModelValue = previousModelValue
    nextTick(() => {
      scrollToModelValue(modelValue)
    })
  })

  watch(
    tabOptions,
    () => {
      nextTick(() => {
        setWidth()
      })
    },
    { deep: true },
  )

  resizeObserver = new ResizeObserver(() => {
    setWidth()
  })
  resizeObserver.observe(tabPanelsRef.value)

  nextTick(() => {
    setWidth()
  })
  if (props.hash) {
    // server sideではrouteのhashは検知できない
    const route = useRoute()
    watch(
      () => route.hash,
      () => {
        if (route.hash) {
          const val = route.hash.substring(1)
          if (state.tabOptions.some(tpo => tpo.val === val && !tpo.disabled)) {
            emits('update:modelValue', val)
          }
        }
      },
      { immediate: true },
    )
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})

const setWidth = () => {
  const size = tabPanelsRef?.value.getBoundingClientRect()
  width.value = size?.width || 0
  scrollToModelValue(props.modelValue, false)
}

// アクション
const clickTab = (val: string, e: Event) => {
  if (!props.hash) {
    emits('update:modelValue', val)
  }
}

// スライド処理

function scrollToModelValue(val: string | null, animate = true) {
  const index = activeTabs.value.findIndex(opt => opt.val === val)
  if (index !== -1 && tabPanelsRef.value) {
    if (state.previousModelValue !== undefined) {
      const previousindex = activeTabs.value.findIndex(
        opt => opt.val === state.previousModelValue,
      )
      if (previousindex !== -1) {
        tabPanelsRef.value.scrollTo({ left: previousindex * width.value })
      }
    }
    tabPanelsRef.value.scrollTo({
      left: index * width.value,
      behavior: props.animate && animate ? 'smooth' : undefined,
    })
  }
}

// スライド操作

function onMoveStart(event: MouseEvent | TouchEvent) {
  if (props.swipeable) {
    state.start = true
    const position = getPosition(event)
    if (!position) {
      return
    }
    state.startPosition.x = position.x
    state.startPosition.y = position.y
  }
}

function onMoveEnd(event: MouseEvent | TouchEvent) {
  if (state.start) {
    state.start = false
    const position = getPosition(event)
    if (!position) {
      return
    }
    const selectedIndex = activeTabs.value.findIndex(
      option => option.val === props.modelValue,
    )
    const dx = state.startPosition.x - position.x
    if (dx < -props.threshold) {
      if (selectedIndex > 0) {
        const tabPanelOption = activeTabs.value[selectedIndex - 1]
        activateTab(tabPanelOption.val)
      }
    } else if (dx > props.threshold) {
      if (selectedIndex < activeTabs.value.length - 1) {
        const tabOption = activeTabs.value[selectedIndex + 1]
        activateTab(tabOption.val)
      }
    }
  }
}

const router = useRouter()

function activateTab(tab: string) {
  if (props.hash) {
    router.push({ hash: '#' + tab })
  } else {
    emits('update:modelValue', tab)
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.base-tab {
  width: 100%;

  .is-alert .tab-button {
    display: flex;
    gap: 0.25rem;
    color: $jps-key-red;

    &::after {
      content: "*";
      font-weight: normal;
      color: $jps-key-red;
    }
  }

  .tab-button-icon {
    flex-shrink: 0;
  }
}

.tab-style {
  &.base-tab-head {
    display: flex;
    overflow: hidden;
  }

  .base-tab-list-wrapper {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: thin;
    @include custom-scroll-bar();
  }

  .base-tab-list {
    display: flex;
    flex-grow: 1;
    flex-shrink: 0;
    white-space: nowrap;

    border: 1px solid $jps-gray-300;
    border-radius: 4px;
    background: $white;
    overflow: hidden;
    width: fit-content;
    min-width: 100%;
  }

  .base-tab {
    border-bottom: 1px solid transparent;
    display: flex;
    align-items: center;
    width: fit-content;

    &:hover {
      button {
        color: $jps-key-blue-hover;
      }
    }

    a,
    button {
      padding: 0.5rem 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: $size-6;
      gap: 0.25rem;
    }

    a {
      text-decoration: none;
      color: $black;
      text-decoration-line: none;
    }

    button {
      line-height: 1rem;
      background-color: transparent;
      padding: 12px;
      cursor: pointer;
    }

    &.is-active {
      border-right: 1px solid $jps-gray-300;
      border-left: 1px solid $jps-gray-300;
      border-bottom: 2px solid $jps-gray-600;

      &:first-child {
        border-left: 0;
      }

      a,
      button {
        background-color: $white;
        color: $jps-key-blue;
        font-weight: 700;
        cursor: default;
      }
    }

    &.is-disabled {
      pointer-events: none;

      .tab-button::after {
        content: ''; // "*"を除去
      }

      a,
      button {
        color: $text-grey;
        pointer-events: none;
      }
    }
  }
}

.switch-style {
  .base-tab-head {
    display: flex;
    justify-content: center;
  }

  .base-tab-list {
    background-color: $jps-gray-200;
    box-shadow: inset 0px 1px 1px rgba(0, 0, 0, 0.16);
    border-radius: 999px;
    padding: 0.25rem;
  }

  .base-tab {
    flex-grow: 1;
    display: flex;

    &.is-active {
      background-color: white;
      box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
      border-radius: 999px;

      a,
      button {
        pointer-events: none;
      }
    }

    a {
      text-decoration: none;
    }
    button {
      background-color: transparent;
    }
    a,
    button {
      padding: 8px 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
      flex-grow: 1;
      cursor: pointer;
    }
    &.is-disabled {
      pointer-events: none;

      a,
      button {
        /* @include font-color(sub, !important); */
        pointer-events: none;
      }
    }
  }
}

.base-tab-panels {
  white-space: nowrap;
  overflow: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
